import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { UseFormRegister, UseFormHandleSubmit, UseFormSetValue, UseFormWatch, UseFormGetValues, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { progressivityService, type Progressivity } from '@/api/domains/attendance';
import { progressivityFormSchema, type ProgressivityFormValues } from '@/schemas/attendance';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { formatDateToYYYYMMDD } from '@/utils';


interface UseProgressivityFormOptions {
  record?: Progressivity;
  onSuccess?: () => void;
}

export interface ProgressivityFormSubset {
  register: UseFormRegister<ProgressivityFormValues>;
  handleSubmit: UseFormHandleSubmit<ProgressivityFormValues>;
  setValue: UseFormSetValue<ProgressivityFormValues>;
  watch: UseFormWatch<ProgressivityFormValues>;
  getValues: UseFormGetValues<ProgressivityFormValues>;
  errors: FieldErrors<ProgressivityFormValues>;
  isSubmitted: boolean;
}

export interface UseProgressivityFormResult {
  form: ProgressivityFormSubset;
  isEditMode: boolean;
  isReady: boolean;
  onSubmit: (data: ProgressivityFormValues) => Promise<void>;
}

export function useProgressivityForm(
  { record, onSuccess }: UseProgressivityFormOptions,
): UseProgressivityFormResult {
  const { t: translate } = useTranslation();
  const { closeSidePanel, showNotification, showApiError } = useUI();

  const isEditMode = Boolean(record);

  const formMethods = useForm<ProgressivityFormValues>({
    resolver: zodResolver(progressivityFormSchema),
  });

  const { setValue, watch, formState: { isSubmitting } } = formMethods;

  const startDate = watch('startDate');
  const weeklyHours = watch('weeklyHours');
  const isReady = Boolean(startDate) && Boolean(weeklyHours) && weeklyHours > 0 && !isSubmitting;

  useEffect(() => {
    if (record) {
      setValue('startDate', formatDateToYYYYMMDD(new Date(record.rawStartDate)));
      setValue('weeklyHours', record.weeklyHours);
    }
  }, [record, setValue]);

  const onSubmit = async (data: ProgressivityFormValues) => {
    try {
      let response;
      if (isEditMode && record) {
        response = await progressivityService.updateProgressivity(record.rawStartDate, data);
      } else {
        response = await progressivityService.createProgressivity(data);
      }

      if (response.success) {
        const successMessage = isEditMode
          ? translate('attendance:configuration.workday.messages.editSuccess')
          : translate('attendance:configuration.workday.messages.createSuccess');
        showNotification('success', successMessage);
        closeSidePanel();
        onSuccess?.();
      } else {
        if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, formMethods);
          return;
        }
        showApiError(response);
      }
    } catch {
      showNotification('error', translate('common:errors.unexpected'));
    }
  };

  return {
    form: {
      register: formMethods.register,
      handleSubmit: formMethods.handleSubmit,
      setValue: formMethods.setValue,
      watch: formMethods.watch,
      getValues: formMethods.getValues,
      errors: formMethods.formState.errors,
      isSubmitted: formMethods.formState.isSubmitted,
    },
    isEditMode,
    isReady,
    onSubmit,
  };
}
